import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, NotebookPen, Plus, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { useCreatePatientNote, usePatientNotes, useUpdatePatientNote } from '@/hooks/queries';
import { useAuthStore } from '@/auth';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { extractApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import type { PatientNoteResponse } from '@/api/patientNotes';

/**
 * Care-team note history on a patient.
 *
 * `variant="card"` — Summary page card.
 * `variant="panel"` — sheet / drawer body (no nested card chrome; composer
 * pinned at the bottom while the history scrolls).
 */
type Props = {
  patientId: number;
  variant?: 'card' | 'panel';
};

const formatNoteTime = (iso?: string) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const isAuthor = (note: PatientNoteResponse, clinicianId: number | null) =>
  clinicianId != null && String(note.created_by_user_id) === String(clinicianId);

export const PatientNoteCard = ({ patientId, variant = 'card' }: Props) => {
  const { t } = useTranslation();
  const clinician = useAuthStore((s) => s.clinician);
  const clinicianId = clinician?.id ?? null;

  const notesQuery = usePatientNotes(patientId);
  const create = useCreatePatientNote(patientId);
  const update = useUpdatePatientNote(patientId);

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const handleAdd = async () => {
    const text = draft.trim();
    if (!text || create.isPending) return;
    try {
      await create.mutateAsync(text);
      setDraft('');
      toast({ title: t('Note saved') });
      track(ANALYTICS_EVENTS.PATIENT_NOTE_SAVED, { note_length: text.length });
    } catch (err) {
      toast({
        title: t('Could not save the entry.'),
        description: extractApiErrorMessage(err),
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (noteId: number) => {
    const text = editDraft.trim();
    if (!text || update.isPending) return;
    try {
      await update.mutateAsync({ noteId, note: text });
      setEditingId(null);
      setEditDraft('');
      toast({ title: t('Note saved') });
    } catch (err) {
      toast({
        title: t('Could not save the entry.'),
        description: extractApiErrorMessage(err),
        variant: 'destructive',
      });
    }
  };

  const notes = notesQuery.data ?? [];
  const panel = variant === 'panel';

  const history = (
    <>
      {notesQuery.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('No notes yet. Add the first note for this patient.')}
        </p>
      ) : (
        <ul className={cn('space-y-3', panel && 'pb-2')}>
          {notes.map((note) => {
            const own = isAuthor(note, clinicianId);
            const authorLabel = own
              ? t('You')
              : t('Clinician {{id}}', { id: note.created_by_user_id ?? '' });
            const stamped = formatNoteTime(note.created_time);
            const editing = editingId != null && editingId === note.id;

            return (
              <li
                key={note.id ?? `${note.created_time}-${note.note}`}
                className="rounded-md border border-border bg-card px-3 py-2.5"
              >
                <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="min-w-0 truncate">
                    {authorLabel}
                    {stamped ? ` · ${stamped}` : ''}
                  </span>
                  {own && note.id != null && !editing && (
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-primary underline-offset-2 hover:underline"
                      onClick={() => {
                        setEditingId(note.id!);
                        setEditDraft(note.note);
                      }}
                    >
                      {t('Edit')}
                    </button>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={4}
                      className={cn(
                        'w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      )}
                      disabled={update.isPending}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft('');
                        }}
                        disabled={update.isPending}
                      >
                        {t('Cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleUpdate(note.id!)}
                        disabled={update.isPending || !editDraft.trim()}
                      >
                        {update.isPending ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="mr-1 h-3.5 w-3.5" />
                        )}
                        {t('Save')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{note.note}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {notesQuery.isError && (
        <p className="text-sm text-destructive">
          {extractApiErrorMessage(notesQuery.error) ?? t('Failed to load notes.')}
        </p>
      )}
    </>
  );

  const composer = (
    <div className={cn('space-y-2', panel && 'border-t bg-background pt-3')}>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={t('Add a note about this patient. The care team can see it.')}
        rows={panel ? 4 : 3}
        className={cn(
          'w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          create.isPending && 'opacity-60',
        )}
        disabled={create.isPending}
      />
      <div className="flex justify-end">
        <Button size="sm" onClick={handleAdd} disabled={create.isPending || !draft.trim()}>
          {create.isPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="mr-1 h-3.5 w-3.5" />
          )}
          {t('Add note')}
        </Button>
      </div>
    </div>
  );

  if (panel) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">{history}</div>
        {composer}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <NotebookPen className="h-4 w-4 text-muted-foreground" />
          {t('Clinician notes')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {history}
        {composer}
      </CardContent>
    </Card>
  );
};
